import { KIND, resolveKind, isRenderable } from './kinds.js'
import { OptionSet } from './OptionSet.jsx'
import { NumericEntry } from './NumericEntry.jsx'
import { TimeEntry } from './TimeEntry.jsx'
import { BinaryPlusText } from './BinaryPlusText.jsx'
import { FreeText } from './FreeText.jsx'

/*
 * Six KINDS, four components. Ordinal, binary and categorical are the same
 * control with different option counts, so they are one component (DS-2 §3.2)
 * rather than three that must be kept in agreement. Dispatch is still on KIND
 * and nothing here knows an instrument's name.
 */
const RENDERERS = {
  [KIND.ORDINAL]: OptionSet,
  [KIND.BINARY]: OptionSet,
  [KIND.CATEGORICAL]: OptionSet,
  [KIND.NUMERIC]: NumericEntry,
  [KIND.TIME]: TimeEntry,
  [KIND.BINARY_PLUS_TEXT]: BinaryPlusText,
}

/**
 * Takes an item and its format and picks a control by KIND.
 *
 * This component knows nothing about PHQ-9, AUDIT, EPDS or any other
 * instrument, and it must stay that way. If a component here ever gets named
 * after an instrument, the design has been lost.
 *
 * @param {object}   item     {item_id, prompt, response_format, is_safety}
 * @param {object}   format   the entry from the config format map
 * @param {object}   answer   {value, text} or null
 * @param {function} onChange receives the next {value, text}
 * @param {'stacked'|'row'} variant  'row' is a grid cell: prompt lives in the row label
 */
export function ItemRenderer({
  item,
  format,
  answer,
  onChange,
  disabled = false,
  variant = 'stacked',
  showPrompt = true,
  ariaLabelledBy,
  idPrefix = '',
}) {
  const kind = resolveKind(format)
  const name = `${idPrefix}${item.item_id}`
  const prompt = item.prompt ?? item.text ?? item.stem ?? ''

  if (!isRenderable(format)) {
    return <UnavailableItem item={item} prompt={showPrompt ? prompt : null} />
  }

  const Control = RENDERERS[kind]

  const control = (
    <>
      <Control
        item={item}
        format={format}
        answer={answer}
        onChange={onChange}
        disabled={disabled}
        name={name}
        // A grid cell is row layout with its labels drawn once in the sticky
        // header, so the cell carries a mark only.
        layout={variant === 'row' ? 'row' : undefined}
        // Named by the <legend> when there is one, by the grid row's own
        // prompt element when there is not. Never by a duplicated string.
        ariaLabelledBy={ariaLabelledBy}
      />
      {/* free_text on a kind other than binary just appends the box. One path. */}
      {format.free_text && kind !== KIND.BINARY_PLUS_TEXT ? (
        <FreeText
          format={format}
          answer={answer}
          onChange={onChange}
          disabled={disabled}
          name={name}
        />
      ) : null}
    </>
  )

  if (!showPrompt) return control

  return (
    // data-item-id makes WHICH ITEM IS ON SCREEN observable. The grid rows
    // already carried it; a one-at-a-time stack did not, so nothing outside the
    // component could tell which item a participant was looking at — only read
    // the prompt text and hope. That is why the day-0 jump was invisible to
    // every check: the answers all reached the database, and the only thing
    // that had gone wrong was position, which nothing could see.
    <fieldset
      data-item-id={item?.item_id}
      className="min-w-0 border-0 p-0"
      disabled={disabled}
    >
      <legend className="t-question mb-[var(--s-3)] text-[var(--ink-900)]">{prompt}</legend>
      {control}
    </fieldset>
  )
}

/**
 * A last-resort guard, NOT a designed screen.
 *
 * By contract a participant can never reach this: config marks a format with no
 * option text `blocking: true`, and backend excludes any item using one from the
 * plan it hands a participant (config CONTRACT.md s1). So this exists to fail
 * visibly in development rather than to be read by anyone in the study, and no
 * participant-facing copy should be invested in it.
 *
 * The caller treats the item as unanswerable, so a safety item that somehow
 * landed here cannot deadlock the battery.
 */
function UnavailableItem({ item, prompt }) {
  return (
    <div
      data-unavailable="1"
      className="rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-sunken)] px-4 py-3"
    >
      {prompt ? <p className="t-question mb-2 text-[var(--ink-900)]">{prompt}</p> : null}
      <p className="text-sm text-[var(--ink-900)]">This one isn&rsquo;t available. Carry on to the next.</p>
      {import.meta.env.DEV ? (
        <p className="mt-2 font-mono text-xs text-[var(--ink-700)]">
          CONTRACT BREACH: {item.item_id} &rarr; format &ldquo;{item.response_format}&rdquo; has no
          options. Backend should have excluded this item from the plan.
        </p>
      ) : null}
    </div>
  )
}
